import { useState } from 'react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { EnhancedInput as Input } from './EnhancedInput';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import Logo from './Logo';
import ordnr2Image from '../assets/ordnr2.jpg';

export interface QuestionnaireData {
  familyStatus: 'single' | 'couple';
  childrenCount: number;
}

interface AuthenticatedQuestionnaireProps {
  onComplete: (data: QuestionnaireData) => void;
  onSkip: () => void;
}

export default function AuthenticatedQuestionnaire({ onComplete, onSkip }: AuthenticatedQuestionnaireProps) {
  const { memberstack } = useAuth();
  const { t } = useI18n();
  const [currentStep, setCurrentStep] = useState(1);
  const [data, setData] = useState<QuestionnaireData>({
    familyStatus: 'single',
    childrenCount: 0
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Step 1 is signup (not shown here), so we show 2/3 and 3/3
  const totalSteps = 3;
  const internalTotalSteps = 2;
  const currentStepNumber = currentStep === 1 ? 2 : 3;
  const progress = (currentStepNumber / totalSteps) * 100;

  const updateData = (field: keyof QuestionnaireData, value: any) => {
    setData(prev => ({ ...prev, [field]: value }));
  };

  const saveQuestionnaireData = async (questionnaireData: QuestionnaireData) => {
    if (!memberstack) {
      setSaveError(t('questionnaire.saveError.noConnection'));
      return false;
    }
    
    try {
      const response = await memberstack.getMemberJSON();
      const currentData = response.data || {};
      
      // Save questionnaire data only (budget generation requires more fields)
      // Budget can be generated later when user completes full questionnaire
      await memberstack.updateMemberJSON({
        json: {
          ...currentData,
          questionnaire: questionnaireData
        }
      });
      
      console.log('Questionnaire data saved to Memberstack');
      return true;
    } catch (error: any) {
      console.error('Failed to save questionnaire data:', error);
      setSaveError(error?.message || t('questionnaire.saveError.failed'));
      return false;
    }
  };

  const nextStep = async () => {
    if (currentStep < internalTotalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      // Complete questionnaire and save to Memberstack
      setIsSaving(true);
      setSaveError('');
      
      const success = await saveQuestionnaireData(data);
      if (success) {
        onComplete(data);
      } else {
        setIsSaving(false);
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const canProceed = () => true;

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="questionnaire-step-content">
            <div className="questionnaire-step-header">
              <h2 className="text-2xl font-semibold text-gray-900">{t('questionnaire.familyStatus.title')}</h2>
              <p className="text-gray-600">{t('questionnaire.familyStatus.subtitle')}</p>
            </div>
            <RadioGroup value={data.familyStatus} onValueChange={(value) => updateData('familyStatus', value)}>
              <div className="questionnaire-radio-grid">
                <Label className="questionnaire-radio-option">
                  <RadioGroupItem value="single" />
                  <div>
                    <div className="font-medium text-gray-900">{t('questionnaire.familyStatus.single')}</div>
                    <div className="text-sm text-gray-600">{t('questionnaire.familyStatus.singleDesc')}</div>
                  </div>
                </Label>
                <Label className="questionnaire-radio-option">
                  <RadioGroupItem value="couple" />
                  <div>
                    <div className="font-medium text-gray-900">{t('questionnaire.familyStatus.couple')}</div>
                    <div className="text-sm text-gray-600">{t('questionnaire.familyStatus.coupleDesc')}</div>
                  </div>
                </Label>
              </div>
            </RadioGroup>
            <div className="questionnaire-input-group">
              <Label className="text-sm font-medium text-gray-700">{t('questionnaire.childrenCount.title')}</Label>
              <Input
                type="number"
                value={data.childrenCount || 0}
                onChange={(e) => updateData('childrenCount', Math.max(0, parseInt(e.target.value || '0', 10)))}
                placeholder={t('questionnaire.childrenCount.placeholder')}
                className="questionnaire-input"
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="questionnaire-step-content">
            <div className="questionnaire-step-header">
              <h2 className="text-2xl font-semibold text-gray-900">{t('questionnaire.completion.title')}</h2>
              <p className="text-gray-600">{t('questionnaire.completion.subtitle')}</p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="questionnaire-page">
      {/* Logo */}
      <div className="questionnaire-logo">
        <Logo className="w-14 h-14" />
      </div>
      
      <div className="questionnaire-container">
        <div className="questionnaire-layout">
          {/* Left Column - Form Content */}
          <div className="questionnaire-form-column">
            {/* Progress */}
            <div className="questionnaire-progress-section">
              <div className="questionnaire-progress-header">
                <div className="questionnaire-progress-text">
                  {currentStepNumber} / {totalSteps}
                </div>
                <div className="questionnaire-progress-bar-container">
                  <div 
                    className="questionnaire-progress-bar"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="questionnaire-content">
              {renderStep()}
            </div>

            {/* Error Message */}
            {saveError && (
              <div className="questionnaire-error">
                {saveError}
              </div>
            )}

            {/* Navigation */}
            <div className="questionnaire-nav">
              {currentStep < internalTotalSteps ? (
                <>
                  <div className="questionnaire-nav-left">
                    {currentStep > 1 && (
                      <Button
                        variant="outline"
                        onClick={prevStep}
                        className="questionnaire-button-outline"
                        disabled={isSaving}
                      >
                        <ArrowLeft className="icon-sm mr-2" />
                        {t('questionnaire.back')}
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      onClick={onSkip}
                      className="questionnaire-button-skip"
                      disabled={isSaving}
                    >
                      {t('questionnaire.skip')}
                    </Button>
                  </div>
                  <Button
                    onClick={nextStep}
                    disabled={!canProceed() || isSaving}
                    className="questionnaire-button-primary"
                  >
                    {t('questionnaire.next')}
                    <ArrowRight className="icon-sm ml-2" />
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outline"
                    onClick={() => window.location.href = '/dashboard'}
                    className="questionnaire-button-outline"
                    disabled={isSaving}
                  >
                    {t('questionnaire.completion.goToDashboard')}
                  </Button>
                  <Button
                    onClick={nextStep}
                    disabled={isSaving}
                    className="questionnaire-button-primary"
                  >
                    {isSaving ? (
                      <>
                        <div className="spinner-border-sm"></div>
                        {t('questionnaire.saving')}
                      </>
                    ) : (
                      <>
                        {t('questionnaire.completion.startFineTuning')}
                        <ArrowRight className="icon-sm ml-2" />
                      </>
                    )}
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Right Column - Visual */}
          <div className="questionnaire-visual-column">
            <div className="questionnaire-visual-inner">
              {/* Visual content matching production design */}
              <div className="questionnaire-visual-content">
                <img 
                  src={ordnr2Image} 
                  alt="Questionnaire Visual" 
                  className="questionnaire-image"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}